import { $fetch } from '@/boot/fetch'
import { replacePathTokens } from '@/utils/routes.util'
import { startCase } from 'lodash-es'
import { currency, dateMDYShortWithTime, dateMDY } from '@/utils/formatter.util'
import { validateQueryParams } from '@/utils/validator.util'
import { dateRangeObjectToApiFormat } from '@/utils/dates.util.js'
import { RETURN_STATUS } from '@/constants/userReturns.const'
import { getTrackingStatusColor, getTrackingStatusLabel } from '@/utils/tracking.util'

const BASE_ENDPOINT = '/retailers/{{retailerId}}/reports'

// util method to validate and convert dateRange param
function validateDateRange(dateRange) {
  if (typeof dateRange === 'object' && dateRange.from && dateRange.to) {
    return dateRangeObjectToApiFormat({ ...dateRange })
  }
  return dateRange
}

/**
 * @description Get color code for return status
 * @param {String} returnStatus
 * @returns {String} color code
 */
function getReturnItemStatusColor(returnStatus) {
  switch (returnStatus) {
    case RETURN_STATUS.COMPLETE:
    case RETURN_STATUS.HANDED_OFF:
      return 'positive'
    case RETURN_STATUS.IN_PROGRESS:
      return 'blue'
    default:
      return 'grey'
  }
}

/**
 * @description Format return item data for display
 * @param {Object} item
 * @returns {Object} formatted item
 */
// PEG-757: human-readable labels for known return reason codes. Used
// as a fallback when the DB `return_reasons.label` is empty, equals a
// generic backfill string, or matches the raw enum code (a known data
// quality issue). The "Damaged in Shipping" entry is required by
// PEG-757 AC4 so retailers see the specific shipping-damage signal
// even though the disposition bucket stays `Defective`.
const RETURN_REASON_CODE_LABELS = {
  DAMAGED_IN_SHIPPING: 'Damaged in Shipping',
  MANUFACTURING_DEFECT: 'Manufacturing Defect',
  DEFECTIVE_ON_ARRIVAL: 'Defective on Arrival',
  MISSING_PARTS: 'Missing Parts',
  WRONG_ITEM_SENT: 'Wrong Item Sent',
  NOT_AS_DESCRIBED: 'Not as Described',
  QUALITY_BELOW_EXPECTATIONS: 'Quality Below Expectations',
  FUNCTIONALITY_PROBLEMS: 'Functionality Problems',
  COLOR_SIZE_MATERIALLY_DIFFERENT: 'Color/Size Materially Different',
  FALSE_ADVERTISING: 'False Advertising',
  CHANGED_MIND: 'Changed Mind',
  DOES_NOT_FIT_SPACE: 'Does Not Fit Space',
  STYLE_COLOR_PREFERENCE: 'Style/Color Preference',
  NO_LONGER_NEEDED: 'No Longer Needed',
  FOUND_BETTER_PRICE: 'Found Better Price',
  ORDERED_WRONG_ITEM: 'Ordered Wrong Item',
  DOES_NOT_MATCH_EXISTING: 'Does Not Match Existing',
  IMPULSE_PURCHASE_REGRET: 'Impulse Purchase Regret',
  PERSONAL_FINANCIAL_REASONS: 'Personal Financial Reasons',
}

function formatReturnReasonLabel(rawLabel, rawCode) {
  // Prefer an explicit human-readable label when the DB row has one.
  // Compare case-insensitively so that a lowercased enum stored as a
  // label (e.g. `damaged_in_shipping`) is not mistaken for human text.
  const trimmed = rawLabel?.trim()
  const upper = trimmed?.toUpperCase()
  if (trimmed && upper !== 'DEFAULT BACKFILL' && upper !== rawCode) {
    // Looks human-readable (has lowercase, no underscores)? Use as-is.
    if (/[a-z]/.test(trimmed) && !trimmed.includes('_')) return trimmed
  }
  if (rawCode && RETURN_REASON_CODE_LABELS[rawCode]) {
    return RETURN_REASON_CODE_LABELS[rawCode]
  }
  if (rawLabel) return startCase(rawLabel.toLowerCase())
  if (rawCode) return startCase(rawCode.toLowerCase())
  return '—'
}

// PEG-894: human-readable labels for return_session_items.resolution_choice.
// Mirrors the backend ResolutionChoiceEnum (schemas/retailersReports.ts);
// REFUND_FALLBACK is the "no exchange selected → standard refund" path.
const RESOLUTION_CHOICE_LABELS = {
  REPLACEMENT: 'Replacement',
  DIFFERENT_ITEM: 'Different Item',
  REFUND_FALLBACK: 'Refund (Fallback)',
}

function formatResolutionChoiceLabel(choice) {
  if (!choice) return null
  return RESOLUTION_CHOICE_LABELS[choice] || startCase(choice.toLowerCase())
}

function formatReturnItemDisplay(item) {
  item.customer._fullName = `${item.customer.firstName} ${item.customer.lastName}`
  item.customer._fullNameLastFirst = `${item.customer.lastName}, ${item.customer.firstName}`
  item._returnStatus = { label: startCase(item.returnStatus.toLowerCase()), color: getReturnItemStatusColor(item.returnStatus) }
  // PEG-757: preserve specific reason labels (e.g. "Damaged in
  // Shipping") instead of flattening to the generic disposition bucket.
  item._returnReasonLabel = formatReturnReasonLabel(item.returnReasonLabel, item.returnReasonCode)
  item._classification = {
    label: startCase(item.classification.toLowerCase()).replace(' ', '-'),
    color: item.classification === 'PREFERENCE' ? 'grey' : 'negative',
  }
  item._price = currency(item.price)
  item._dateCreated = dateMDYShortWithTime(item.dateCreated)
  // PEG-334: shipping tracking badge based on least-progressed status across all boxes
  if (item.leastProgressedTrackingStatus !== undefined) {
    item._trackingStatus = formatTrackingStatusBadge(item.leastProgressedTrackingStatus)
  }
  // PEG-894: human-readable label for the resolution_choice column. Mirrors the
  // backend ENUM (REPLACEMENT | DIFFERENT_ITEM | REFUND_FALLBACK); null when the
  // customer was never asked / has not yet chosen.
  item._resolutionChoice = formatResolutionChoiceLabel(item.resolutionChoice)
  return item
}

function formatTrackingStatusBadge(status) {
  return {
    label: getTrackingStatusLabel(status),
    color: getTrackingStatusColor(status),
    raw: status || null,
  }
}

function getCustomerRiskTierColor(tier) {
  switch (tier) {
    case 'CRITICAL':
      return 'negative'
    case 'HIGH':
      return 'orange'
    case 'MEDIUM':
      return 'warning'
    default:
      return 'positive'
  }
}

function formatReturnCustomerDisplay(c) {
  c._fullName = `${c.firstName} ${c.lastName}`
  c._fullNameLastFirst = `${c.lastName}, ${c.firstName}`
  c._riskTier = { label: startCase(c.riskTier.toLowerCase()), color: getCustomerRiskTierColor(c.riskTier) }
  c._firstReturnDate = dateMDY(c.firstReturnDate)
  c._lastReturnDate = dateMDY(c.lastReturnDate)
  c._isBlocked = !c.allowCriticalRiskReturns
  c._status = c._isBlocked ? 'Blocked' : 'Active'

  return c
}

export default {
  /**
   * @description Get returns performance summary with KPI metrics and return reasons breakdown
   * @function getReturnsSummary
   * @param {String} retailerId - The retailer ID
   * @param {String} dateRange - Pipe-delimited timestamps (e.g., "1609459200000|1612137600000")
   * @returns {Promise}
   */
  async getReturnsSummary(retailerId, dateRange) {
    const endpoint = `${BASE_ENDPOINT}/returns/items/summary`
    const url = replacePathTokens(endpoint, { retailerId })

    return $fetch.peg(url, {
      params: { dateRange: validateDateRange(dateRange) },
    })
  },

  /**
   * @description Get detailed list of all returns with optional filtering
   * @function getAllReturns
   * @param {String} retailerId - The retailer ID
   * @param {Object} params - Params object
   * @param {String} params.dateRange - Pipe-delimited timestamps (e.g., "1609459200000|1612137600000")
   * @param {String} params.reasons - Comma-separated return reasons
   * @param {String} params.dispositions - Comma-separated dispositions (PREFERENCE, NON_PREFERENCE)
   * @param {String} params.trackingStatuses - Comma-separated tracking statuses to filter by (e.g. delivered,in_transit,pre_transit,failure). Multi-box semantics: a return matches if any box has a matching status.
   * @param {String} params.searchQuery - Fuzzy search on order #, email, name, or SKU
   * @param {Boolean} params.wrongItem - Filter for returns with wrong item received classification
   * @param {String} params.damageSources - Comma-separated damage source enums (e.g. CARRIER_DAMAGE,INTERNAL_PACKAGING)
   * @param {String} params.resolutionChoice - Comma-separated customer resolution choices (REPLACEMENT, DIFFERENT_ITEM, REFUND_FALLBACK)
   * @param {String} params.limit - Max results per page (default: 10, max: 100)
   * @param {String} params.offset - Number of results to skip (default: 0)
   * @returns {Promise}
   */
  async getAllReturns(retailerId, params = {}) {
    const endpoint = `${BASE_ENDPOINT}/returns/items`
    const url = replacePathTokens(endpoint, { retailerId })

    params = validateQueryParams(params, [
      'dateRange',
      'reasons',
      'dispositions',
      'trackingStatuses',
      'damageSources',
      'resolutionChoice',
      'searchQuery',
      'wrongItem',
    ])
    params.dateRange = validateDateRange(params.dateRange)

    return $fetch.peg(url, { params }).then(resp => {
      resp.returnItems = resp.returnItems.map(formatReturnItemDisplay)
      return resp
    })
  },

  /**
   * @description Get details for a single return
   * @function getSingleReturn
   * @param {String} retailerId - The retailer ID
   * @param {String} returnItemId - Return session item ID
   * @returns {Promise}
   */
  async getSingleReturn(retailerId, returnItemId) {
    const endpoint = `${BASE_ENDPOINT}/returns/items/{{returnItemId}}`
    const url = replacePathTokens(endpoint, { retailerId, returnItemId })

    return $fetch.peg(url).then(resp => {
      resp.returnItem = formatReturnItemDisplay(resp.returnItem)
      return resp
    })
  },

  /**
   * @description Get auction items summary
   * @function getAuctionItemsSummary
   * @param {String} retailerId - The retailer ID
   * @param {String} dateRange - Pipe-delimited timestamps
   * @returns {Promise}
   */
  async getAuctionItemsSummary(retailerId, dateRange) {
    const endpoint = `${BASE_ENDPOINT}/auctionItems/summary`
    const url = replacePathTokens(endpoint, { retailerId })

    const params = { dateRange: validateDateRange(dateRange) }

    return $fetch.peg(url, { params })
  },

  /**
   * @description Get all auction items with optional filtering
   * @function getAllAuctionItems
   * @param {String} retailerId - The retailer ID
   * @param {Object} params - Optional params object
   * @param {String} params.dateRange - Pipe-delimited timestamps
   * @param {String} params.status - Comma-separated statuses (ACTIVE, SOLD, LIQUIDATED)
   * @returns {Promise}
   */
  async getAllAuctionItems(retailerId, params = {}) {
    const endpoint = `${BASE_ENDPOINT}/auctionItems`
    const url = replacePathTokens(endpoint, { retailerId })

    params = validateQueryParams(params, ['dateRange', 'status'])
    params.dateRange = validateDateRange(params.dateRange)

    return $fetch.peg(url, { params })
  },

  /**
   * @description Get returners risk distribution summary
   * @function getReturnersSummary
   * @param {String} retailerId - The retailer ID
   * @returns {Promise}
   */
  async getReturnersSummary(retailerId) {
    const endpoint = `${BASE_ENDPOINT}/returners/summary`
    const url = replacePathTokens(endpoint, { retailerId })

    return $fetch.peg(url)
  },

  /**
   * @description Get all returners with pagination
   * @function getAllReturners
   * @param {String} retailerId - The retailer ID
   * @param {Object} params - Optional params object
   * @param {String} params.riskTier - Filter by risk tier (LOW, MEDIUM, HIGH, CRITICAL)
   * @param {String} params.limit - Max results per page (default: 10, max: 100)
   * @param {String} params.offset - Number of results to skip (default: 0)
   * @param {String} params.searchQuery - Fuzzy search on email or name
   * @param {Boolean} params.allowReturns - Filter by allowCriticalRiskReturns (true, false)
   * @returns {Promise}
   */
  async getAllReturners(retailerId, params = {}) {
    const endpoint = `${BASE_ENDPOINT}/returners`
    const url = replacePathTokens(endpoint, { retailerId })

    params = validateQueryParams(params, ['riskTier', 'searchQuery', 'allowReturns'])

    return $fetch.peg(url, { params }).then(resp => {
      resp.returners = resp.returners.map(formatReturnCustomerDisplay)
      return resp
    })
  },

  /**
   * @description Get single returner details
   * @function getSingleReturner
   * @param {String} retailerId - The retailer ID
   * @param {String} userId - User ID
   * @returns {Promise}
   */
  async getSingleReturner(retailerId, userId) {
    const endpoint = `${BASE_ENDPOINT}/returners/{{userId}}`
    const url = replacePathTokens(endpoint, { retailerId, userId })

    return $fetch.peg(url).then(resp => {
      resp.returner = formatReturnCustomerDisplay(resp.returner)
      return resp
    })
  },

  /**
   * @description Get return history for a specific returner
   * @function getReturnerReturns
   * @param {String} retailerId - The retailer ID
   * @param {String} params.userId - User ID
   * @param {String} params.limit - Max results per page (default: 10, max: 100)
   * @param {String} params.offset - Number of results to skip (default: 0)
   * @param {String} params.dateRange - Optional pipe-delimited timestamps
   * @returns {Promise}
   */
  async getReturnerReturns(retailerId, params = {}) {
    const endpoint = `${BASE_ENDPOINT}/returners/{{userId}}/returns/items`
    const url = replacePathTokens(endpoint, { retailerId, userId: params.userId })

    params = validateQueryParams(params, ['dateRange'])
    if (params.dateRange) {
      params.dateRange = validateDateRange(params.dateRange)
    }

    return $fetch.peg(url, { params })
  },
}
